import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  clearAuth,
  fetchMe,
  loginRequest,
  logoutRequest,
  refreshAccessToken,
} from "../../api/authToken.js";

// Access-токен на бекенді живе 15 хв (ACCESS_TOKEN_TTL, routes/auth.js).
// Оновлюємо його у фоні раз на 10 хв, поки сесія відкрита, щоб він не встиг
// протермінуватися під час звичайної роботи в застосунку.
//
// Тривалість сесії загалом визначає 7-денна httpOnly refresh-cookie на
// сервері, а не клієнт: access-токен і роль тримаються лише в пам'яті
// (src/api/authToken.js), тож після перезавантаження сторінки сесія
// відновлюється мовчки через POST /api/auth/refresh + GET /api/auth/me.
export const REFRESH_INTERVAL_MS = 10 * 60 * 1000;

export const AuthContext = createContext({
  user: null,
  loading: false,
  login: async () => null,
  logout: async () => {},
});

export const AuthProvider = ({ children }) => {
  // user: {id, username, role} або null.
  const [user, setUser] = useState(null);
  // true, доки не завершилось мовчазне відновлення сесії після
  // перезавантаження -- ProtectedRoute у цей час не редіректить на /login.
  const [loading, setLoading] = useState(true);
  const API = process.env.REACT_APP_API_BASE;
  const refreshTimerRef = useRef(null);

  const stopAutoRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const logout = useCallback(async () => {
    stopAutoRefresh();
    setUser(null);
    await logoutRequest(API);
  }, [API, stopAutoRefresh]);

  const startAutoRefresh = useCallback(() => {
    stopAutoRefresh();
    refreshTimerRef.current = setInterval(async () => {
      const newToken = await refreshAccessToken(API);
      if (!newToken) {
        // 7-денна refresh-cookie протермінувалась або недійсна -- лише тоді
        // справді розлогінюємо. Жорсткий перехід, як і раніше: частина
        // маршрутів (/goout, /acts тощо) не обгорнута в ProtectedRoute і
        // лишилася б на екрані з user = null.
        stopAutoRefresh();
        clearAuth();
        setUser(null);
        window.location.href = "/login";
      }
    }, REFRESH_INTERVAL_MS);
  }, [API, stopAutoRefresh]);

  // Мовчазне відновлення сесії (silent refresh) під час завантаження.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const token = await refreshAccessToken(API);
      const me = token ? await fetchMe(API) : null;
      if (cancelled) return;
      if (me) {
        setUser(me);
        startAutoRefresh();
      } else {
        clearAuth();
        setUser(null);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
      stopAutoRefresh();
    };
  }, [API, startAutoRefresh, stopAutoRefresh]);

  const login = useCallback(async (username, password) => {
    await loginRequest(API, username, password);
    // Роль беремо з відповіді /api/auth/me, а не з тіла /login.
    const me = await fetchMe(API);
    if (!me) {
      clearAuth();
      throw new Error("Не вдалося отримати профіль користувача");
    }
    setUser(me);
    setLoading(false);
    startAutoRefresh();
    return me.role;
  }, [API, startAutoRefresh]);

  const value = useMemo(
    () => ({ user, loading, login, logout }),
    [user, loading, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

//  Окремо експортуємо хук для доступу до контексту
export const useAuth = () => useContext(AuthContext);
